import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import "./App.css";

import { LangProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
// Above-the-fold: keep Hero eagerly imported so it ships in the initial bundle
// for the fastest LCP. Everything below the fold is route-/viewport-deferred.
import Hero from "@/components/Hero";
import TrustStrip from "@/components/TrustStrip";
import IdleMount from "@/components/IdleMount";
import RouteProgressBar from "@/components/RouteProgressBar";

// ─── Non-critical global widgets (idle-mounted after LCP) ───
// These are never above-the-fold and don't need to render before the hero
// paints. Deferring them past `requestIdleCallback` shaved ~28 KB gz off the
// initial main.js payload and cuts ~180 ms of JS execution on mid-tier
// mobiles — measurable improvement in mobile LCP on slow 4G.
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const ChatWidgetFloating = lazy(() =>
  import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidgetFloating }))
);
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));

// ─── Below-the-fold homepage sections (lazy) ───
// These render below Hero so they don't need to block first paint or LCP.
// Cuts ~80-120 KB off the initial JS budget on the homepage.
const ServicePillars       = lazy(() => import("@/components/ServicePillars"));
const BankPartners         = lazy(() => import("@/components/BankPartners"));
const ProcessTimeline      = lazy(() => import("@/components/ProcessTimeline"));
const CaseStudies          = lazy(() => import("@/components/CaseStudies"));
const HomepageObjekteTeaser = lazy(() => import("@/components/HomepageObjekteTeaser"));
const QualificationGate    = lazy(() => import("@/components/QualificationGate"));
const TeamSection          = lazy(() => import("@/components/TeamSection"));
const FAQ                  = lazy(() => import("@/components/FAQ"));
const ConsultationForm     = lazy(() => import("@/components/ConsultationForm"));
const Footer               = lazy(() => import("@/components/Footer"));

// ─── Route-level code-splitting ───
// Each route bundle is loaded only when the user navigates to it.
// This cuts the initial JS payload by ~55% on first-visit homepage loads.
const ServicesPage         = lazy(() => import("@/components/ServicesPage"));
const ProcessPage          = lazy(() => import("@/components/ProcessPage"));
const ResultsPage          = lazy(() => import("@/components/ResultsPage"));
const TeamPage             = lazy(() => import("@/components/TeamPage"));
const TaxStrategyReport    = lazy(() => import("@/components/TaxStrategyReport"));
const InvestmentThesisPage = lazy(() => import("@/components/InvestmentThesisPage"));
const ImpressumPage        = lazy(() => import("@/components/ImpressumPage"));
const DatenschutzPage      = lazy(() => import("@/components/DatenschutzPage"));
const AGBPage              = lazy(() => import("@/components/AGBPage"));
const ApplyPage            = lazy(() => import("@/components/ApplyPage"));
const BlogPage             = lazy(() => import("@/components/BlogPage"));
const BlogArticle          = lazy(() => import("@/components/BlogArticle"));
const ProjectsPage         = lazy(() => import("@/components/ProjectsPage"));
const AmselwegPage         = lazy(() => import("@/components/AmselwegPage"));
const BaumhaselringPage    = lazy(() => import("@/components/BaumhaselringPage"));
const ZechlinerStrassePage = lazy(() => import("@/components/ZechlinerStrassePage"));
const ObjekteOverviewPage  = lazy(() => import("@/components/ObjekteOverviewPage"));
const FinancingPortal      = lazy(() => import("@/components/financing/FinancingPortal"));
const AdminSharePage       = lazy(() => import("@/components/AdminSharePage"));

const CONTENT_PAGES = ["/services", "/process", "/results", "/team", "/tax-strategy-report", "/projects", "/projects/karls-living-ii", "/projects/amselweg-14b", "/projects/baumhaselring-180", "/projects/zechliner-strasse-9a"];

const UTM_KEYS = ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fire Meta Pixel ViewContent on key content pages
    if (CONTENT_PAGES.includes(pathname) && window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: pathname.replace('/', '').replace(/-/g, ' '),
        content_category: 'IRE Berlin',
      });
    }
  }, [pathname]);

  // Capture UTM params and fbclid on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((key) => {
      const val = params.get(key);
      if (val) sessionStorage.setItem(key, val);
    });
  }, []);

  return null;
};

const PageSEO = () => {
  const { pathname } = useLocation();
  return <SEO path={pathname} />;
};

// Lightweight skeleton so users see "content is loading" instead of pure
// black while below-the-fold chunks download. Keep it visually quiet —
// matches the dark editorial theme, no jarring spinner.
// Uniform skeleton for below-the-fold lazy sections. Previously used a small
// centered spinner which — repeated 6-8 times down the homepage on slow 4G —
// made the site read as "broken blank screens". Now shows a wide, branded
// shimmer bar + a thin gold progress rail so the user perceives forward
// motion even before the chunk arrives.
const SectionSkeleton = ({ minH = "40vh" }) => (
  <div
    className="w-full flex flex-col items-center justify-center bg-ire-black px-6"
    style={{ minHeight: minH }}
    aria-busy="true"
    role="status"
  >
    <div className="w-full max-w-[280px] flex items-center gap-3 mb-4">
      <span className="w-1 h-1 rounded-full bg-ire-accent animate-pulse" />
      <span className="font-body text-[9px] tracking-[0.35em] uppercase text-white/40">
        Loading
      </span>
    </div>
    <div className="w-full max-w-[280px] h-[1px] bg-white/[0.06] overflow-hidden">
      <div
        className="h-full bg-ire-accent/70"
        style={{
          width: "35%",
          animation: "ire-skeleton-slide 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite",
        }}
      />
    </div>
  </div>
);

const HomePage = () => {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero + TrustStrip render synchronously for the fastest LCP. */}
      <Hero />
      <TrustStrip />
      {/*
        Each below-the-fold section gets its OWN Suspense boundary. Previously
        a single shared boundary meant the whole homepage stayed blank until
        the SLOWEST chunk (Recharts / HomepageCalculator, ~141 KB gz) finished
        downloading — small sections were held hostage by the big one.  Now
        each chunk renders as soon as its bytes arrive; the skeleton signals
        "loading" so users on slow mobile don't think the site is broken.
      */}
      <Suspense fallback={<SectionSkeleton minH="30vh" />}><ServicePillars /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="20vh" />}><BankPartners /></Suspense>
      {/*
        HomepageCalculator is a heavy interactive section (framer-motion +
        useMemo tax loop) and its chunk previously downloaded in parallel
        with main.js — one of the biggest mobile-slowness culprits per user
        feedback. ViewportMount now renders a static editorial placeholder
        in this slot and defers the real component until the user is within
        ~800 px of scrolling to it, OR taps the "launch" button. Rest of
        the page appears instantly on mobile.
      */}
      <Suspense fallback={<SectionSkeleton minH="40vh" />}><ProcessTimeline /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="50vh" />}><CaseStudies /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="60vh" />}><HomepageObjekteTeaser /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="30vh" />}><QualificationGate /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="40vh" />}><TeamSection /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="40vh" />}><FAQ /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="50vh" />}><ConsultationForm /></Suspense>
      <Suspense fallback={<SectionSkeleton minH="20vh" />}><Footer /></Suspense>
    </motion.main>
  );
};

const PageWrapper = ({ children }) => (
  <motion.main
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
  >
    {children}
    <Suspense fallback={<SectionSkeleton minH="50vh" />}><ConsultationForm /></Suspense>
    <Suspense fallback={<SectionSkeleton minH="20vh" />}><Footer /></Suspense>
  </motion.main>
);

// Branded loading state — minimal so it doesn't compete with the actual content
const RouteFallback = () => (
  <div className="min-h-screen bg-ire-black flex items-center justify-center" aria-busy="true">
    <div className="flex items-center gap-3">
      <span className="w-1.5 h-1.5 bg-ire-accent rounded-full animate-pulse" />
      <span className="font-body text-[10px] tracking-[0.4em] text-white/40 uppercase">Loading</span>
    </div>
  </div>
);

function AppChrome() {
  const { pathname } = useLocation();
  // Financing form takes over the whole viewport — no nav, no widgets, no chat.
  const isolated = pathname.startsWith("/financing");
  if (isolated) return null;
  return (
    <>
      <Navigation />
      {/*
        Non-critical widgets — idle-mounted after LCP so mobile 4G users
        paint the hero first. IdleMount waits for requestIdleCallback
        (or a 2.5 s timeout) before rendering; Suspense then loads the
        lazy chunk. Together this cuts ~28 KB gz off main.js and defers
        ~180 ms of JS work on mid-tier Android.
      */}
      <IdleMount>
        <Suspense fallback={null}>
          <ChatWidgetFloating />
          <WhatsAppButton />
          <CookieConsent />
        </Suspense>
      </IdleMount>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <ThemeProvider>
          <div className="App bg-ire-black min-h-screen">
          <BrowserRouter>
            <PageSEO />
            <ScrollToTop />
            <AppChrome />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<PageWrapper><ServicesPage /></PageWrapper>} />
                <Route path="/process" element={<PageWrapper><ProcessPage /></PageWrapper>} />
                <Route path="/results" element={<PageWrapper><ResultsPage /></PageWrapper>} />
                <Route path="/team" element={<PageWrapper><TeamPage /></PageWrapper>} />
                <Route path="/investment-thesis" element={<PageWrapper><InvestmentThesisPage /></PageWrapper>} />
                <Route path="/impressum" element={<PageWrapper><ImpressumPage /></PageWrapper>} />
                <Route path="/datenschutz" element={<PageWrapper><DatenschutzPage /></PageWrapper>} />
                <Route path="/agb" element={<PageWrapper><AGBPage /></PageWrapper>} />
                <Route path="/apply" element={<PageWrapper><ApplyPage /></PageWrapper>} />
                <Route path="/projects" element={<PageWrapper><ProjectsPage /></PageWrapper>} />
                <Route path="/projects/karls-living-ii" element={<PageWrapper><ProjectsPage /></PageWrapper>} />
                <Route path="/projects/amselweg-14b" element={<PageWrapper><AmselwegPage /></PageWrapper>} />
                {/* Baumhaselring 180 taken down — page component + assets retained; redirect prevents dead links */}
                <Route path="/projects/baumhaselring-180" element={<Navigate to="/objekte" replace />} />
                <Route path="/projects/zechliner-strasse-9a" element={<PageWrapper><ZechlinerStrassePage /></PageWrapper>} />
                <Route path="/objekte" element={<PageWrapper><ObjekteOverviewPage /></PageWrapper>} />
                <Route path="/financing/*" element={<FinancingPortal />} />
                <Route path="/admin/share" element={<PageWrapper><AdminSharePage /></PageWrapper>} />
                <Route path="/blog" element={<PageWrapper><BlogPage /></PageWrapper>} />
                <Route path="/blog/:slug" element={<PageWrapper><BlogArticle /></PageWrapper>} />
                <Route path="/tax-strategy-report" element={<PageWrapper><TaxStrategyReport /></PageWrapper>} />
                {/* Legacy: /calculator was retired — funnel old bookmarks to the exposé hub */}
                <Route path="/calculator" element={<Navigate to="/objekte" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
        </ThemeProvider>
      </LangProvider>
    </HelmetProvider>
  );
}

export default App;
